
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Product } from "@/types/product";
import { useAuth } from "@/hooks/useAuth";
import { ProductFormData } from "@/components/admin/ProductForm";
import { homeMenuItems } from "@/data/homeMenuItems";

export const useProductManager = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      console.log("Buscando produtos...");
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar produtos:", error);
        throw error;
      }

      console.log("Produtos encontrados:", data?.length || 0);
      
      // Se não há produtos e o usuário é admin, fazer seeding automático
      if ((!data || data.length === 0) && user?.app_metadata?.role === "admin") {
        console.log("Nenhum produto encontrado, iniciando seeding...");
        await seedInitialProducts();
        return;
      }
      
      setProducts(data || []);
    } catch (error: any) {
      console.error("Erro detalhado:", error);
      toast({
        title: "Erro ao carregar produtos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.app_metadata?.role]);

  const seedInitialProducts = async () => {
    try {
      console.log("Iniciando seeding de produtos...");
      
      // Converter homeMenuItems para formato Product
      const products = homeMenuItems.slice(0, 10).map((item, index) => ({
        name: item.name,
        description: item.description,
        price: parseFloat(item.price.replace('R$ ', '').replace(',', '.')),
        image_url: item.image,
        category: item.category || "Lanches",
        display_order: item.name.toLowerCase().includes('x salada') ? 1 : index + 2
      }));

      const { data, error } = await supabase
        .from("products")
        .insert(products)
        .select();

      if (error) {
        console.error("Erro ao fazer seeding:", error);
        throw error;
      }

      console.log("Seeding concluído com sucesso!");
      toast({
        title: "Produtos iniciais criados",
        description: "Os produtos foram adicionados ao cardápio com sucesso!"
      });

      // Definir os produtos diretamente para evitar loop
      setProducts(data || []);
    } catch (error: any) {
      console.error("Erro no seeding:", error);
      toast({
        title: "Erro ao criar produtos iniciais",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const checkAdminPermission = () => {
    const isAdmin = user?.app_metadata?.role === "admin";
    console.log("Verificando permissão admin:", {
      userId: user?.id,
      role: user?.app_metadata?.role,
      isAdmin
    });
    
    if (!isAdmin) {
      toast({
        title: "Acesso negado",
        description: "Você precisa ter permissões de administrador para realizar esta ação",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const toggleProductStatus = async (product: Product) => {
    if (!checkAdminPermission()) {
      return;
    }
    
    try {
      console.log("Alterando status do produto:", product.id);
      const { error } = await supabase
        .from("products")
        .update({ is_active: !product.is_active })
        .eq("id", product.id);

      if (error) {
        console.error("Erro ao alterar status do produto:", error);
        throw error;
      }
      
      // Atualizar apenas o produto específico no estado local
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === product.id 
            ? { ...p, is_active: !p.is_active }
            : p
        )
      );
      
      toast({
        title: product.is_active ? "Produto pausado" : "Produto reativado",
        description: `${product.name} foi ${product.is_active ? 'pausado' : 'reativado'} com sucesso!`
      });
    } catch (error: any) {
      console.error("Erro detalhado ao alterar status:", error);
      toast({
        title: "Erro ao alterar status do produto",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveProduct = async (formData: ProductFormData, editingProduct: Product | null) => {
    if (!checkAdminPermission()) {
      return;
    }
    
    if (!formData.name || !formData.price || !formData.category) {
      toast({
        title: "Erro",
        description: "Nome, preço e categoria são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("Salvando produto:", formData);
      console.log("Usuário atual:", {
        id: user?.id,
        role: user?.app_metadata?.role
      });
      
      const productData = {
        name: formData.name.trim(),
        description: formData.description ? formData.description.trim() : null,
        price: parseFloat(formData.price),
        image_url: formData.image_url ? formData.image_url.trim() : null,
        category: formData.category.trim()
      };

      if (editingProduct) {
        console.log("Atualizando produto:", editingProduct.id);
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) {
          console.error("Erro ao atualizar produto:", error);
          throw error;
        }
        
        // Atualizar apenas o produto específico no estado local para manter a posição
        setProducts(prevProducts => 
          prevProducts.map(product => 
            product.id === editingProduct.id 
              ? { ...product, ...productData }
              : product
          )
        );
        
        toast({
          title: "Produto atualizado",
          description: "O produto foi atualizado com sucesso!"
        });
      } else {
        console.log("Criando novo produto:", productData);
        const { error } = await supabase
          .from("products")
          .insert([productData]);

        if (error) {
          console.error("Erro ao criar produto:", error);
          throw error;
        }
        
        toast({
          title: "Produto criado",
          description: "O produto foi criado com sucesso!"
        });
        
        // Apenas buscar produtos novamente para novos produtos
        fetchProducts();
      }
    } catch (error: any) {
      console.error("Erro detalhado ao salvar:", error);
      
      let errorMessage = "Erro desconhecido";
      if (error.code === "42501") {
        errorMessage = "Permissão negada. Verifique se você tem permissões de administrador.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao salvar produto",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const deleteProduct = async (product: Product) => {
    if (!checkAdminPermission()) {
      return;
    }
    
    if (!confirm(`Tem certeza que deseja excluir "${product.name}"?`)) return;

    try {
      console.log("Excluindo produto:", product.id);
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", product.id);

      if (error) {
        console.error("Erro ao excluir produto:", error);
        throw error;
      }

      toast({
        title: "Produto excluído",
        description: "O produto foi excluído com sucesso!"
      });

      fetchProducts();
    } catch (error: any) {
      console.error("Erro detalhado ao excluir:", error);
      toast({
        title: "Erro ao excluir produto",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return {
    products,
    loading,
    fetchProducts,
    saveProduct,
    deleteProduct,
    toggleProductStatus,
    checkAdminPermission
  };
};
